import React, {useEffect, useMemo, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {Box, Stack} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Modal,
} from '@lightricks/react-design-system';
import {FEATURE_TO_ROUTE_MAP, ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useMergedAssetErrors from '@contentlab/hooks/use-merged-asset-errors';
import useReadyAssets from '@contentlab/hooks/use-ready-assets';
import {contentLabActions} from '@contentlab/stores/contentLabStore';
import {FeatureName} from '@contentlab/types/project';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets';
import useNavigation from '@/hooks/use-navigation/useNavigation';
import OnboardingFeatureModal from '../onboarding-feature-modal/OnboardingFeatureModal';
import downloadAndUploadAsset from '../onboarding-feature-modal/utils';
import SampleAssetsModal from '../sample-assets-modal/SampleAssetsModal';
import sampleAssets from '../sample-assets-modal/sampleAssets';
import styles from './OnboardingUploadModal.module.scss';

const FEATURE_SUBTITLE_LOCALE_KEY = {
  [FeatureName.REFORMATTING]:
    'This video will be resized for your selected formats',
  [FeatureName.AI_DUBBING]:
    'This video will be dubbed in one or more languages.',
  [FeatureName.REPHRASING]: 'This video will be rescripted with new wording.',
  [FeatureName.CAPTIONS]: 'Captions will be added to this video.',
  [FeatureName.AI_AVATAR]:
    'Create a UGC-style avatar speaking over this video.',
  [FeatureName.UPSCALE]: 'This video will be enhanced for higher quality.',
};

type OnboardingUploadModalProps = {
  feature: FeatureName;
  isOnboardingModalOpen: boolean;
};

function OnboardingUploadModal({
  feature,
  isOnboardingModalOpen,
}: OnboardingUploadModalProps) {
  // We use a temporary feature name to avoid associating uploaded assets with the feature before the assets are ready.
  const onboardingTempAnalyticsSource = `${CONTENT_LAB_VIEW_NAMES.ONBOARDING_UPLOAD_MODAL}--${feature}`;
  const showBestPractices = feature !== FeatureName.AI_AVATAR;
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const {
    uploadedVideos,
    uploadingFiles,
    assetErrors: clientAssetErrors,
  } = useFeatureAssets(onboardingTempAnalyticsSource);

  const {assetErrors} = useMergedAssetErrors({
    serverAssetErrors: [],
    clientAssetErrors,
    uploadedVideos,
  });

  const [isShowingSampleAssets, setIsShowingSampleAssets] = useState(false);
  const [selectedSampleAssetIndex, setSelectedSampleAssetIndex] = useState<
    number | null
  >(null);
  const processEventHandlers = useProcessEvent();
  const {uiInteraction} = useUiInteractionEvent();

  const {viewPresented} = useViewPresentedEvent();
  const modalPresentationId = useMemo(() => uuid(), []);
  const interactionLocation = CONTENT_LAB_VIEW_NAMES.ONBOARDING_UPLOAD_MODAL;

  const {navigate} = useNavigation();

  const closeModal = () => {
    contentLabActions.setOnboardingTargetFeature(null);
    contentLabActions.setIsOnboardingModalOpen(false);
  };

  const closeModalAndNavigateToFeature = () => {
    if (FEATURE_TO_ROUTE_MAP[feature]) {
      navigate(`${ROUTES.CONTENT_LAB}${FEATURE_TO_ROUTE_MAP[feature]}`);
    } else {
      console.error(
        `Failed to navigate to ${feature}, ${feature} is missing from the featureToRouteMap`
      );
    }
    closeModal();
  };

  const readyAssets = useReadyAssets({
    assetIds: uploadedVideos.map((uploadedVideo) => uploadedVideo.id),
    onReady: (assets) => {
      // once the asset is ready => update the feature to the one the user is currently viewing
      assets.forEach((asset) => {
        const matchingUploadingFile = uploadingFiles[asset.name];
        const matchingUploadedVideo = uploadedVideos.find(
          (uploadedVideo) => uploadedVideo.id === asset.id
        );
        if (matchingUploadingFile) {
          contentLabActions.updateUploadingFile({
            name: matchingUploadingFile.file.name,
            feature,
          });
        }
        if (matchingUploadedVideo) {
          contentLabActions.updateUploadedVideo({
            ...matchingUploadedVideo,
            feature,
          });
        }
      });
      closeModalAndNavigateToFeature();
    },
  });

  const isLoading = useMemo(() => {
    return !!(
      uploadedVideos.length && uploadedVideos.length !== readyAssets.length
    );
  }, [uploadedVideos, readyAssets]);

  useEffect(() => {
    // clear before entering the onboarding
    contentLabActions.removeFeatureUploadingFiles(
      onboardingTempAnalyticsSource
    );
    contentLabActions.removeFeatureUploadedVideos(
      onboardingTempAnalyticsSource
    );
  }, []);

  useEffect(() => {
    if (isOnboardingModalOpen) {
      viewPresented({
        presentation_id: modalPresentationId,
        view_name: CONTENT_LAB_VIEW_NAMES.ONBOARDING_UPLOAD_MODAL,
        view_type: 'modal',
        view_location: feature,
        source: '',
      });
    }
  }, []);

  const headerElement = useMemo(() => {
    if (isShowingSampleAssets) {
      return translate('Pick a sample clip');
    }

    return (
      <Stack gap="8px" boxSizing="border-box">
        <Headline size="md" color={designSystemToken('semantic.fg.primary')}>
          {translate('Upload your video')}
        </Headline>
        <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
          {translate(FEATURE_SUBTITLE_LOCALE_KEY[feature])}
        </Body>
      </Stack>
    );
  }, [isShowingSampleAssets, feature]);

  return (
    <Modal
      contentClassName={styles.modalContent}
      open={isOnboardingModalOpen}
      allowedCloseReasons={
        isLoading ? ['escapeKeyDown', 'closeClick'] : undefined
      }
      showCloseButton
      handleClose={(_, reason) => {
        uiInteraction({
          item_name: 'close_modal',
          interaction_location: interactionLocation,
          interaction_id: uuid(),
          presentation_id: modalPresentationId,
          interaction_details: JSON.stringify({
            close_reason: reason,
          }),
        });
        closeModal();
      }}
      width="600px"
      header={headerElement}
      textAlignment="left"
      footerAlignment="right"
      primaryButtonText={isShowingSampleAssets ? translate('Next') : ''}
      primaryButtonProps={{
        disabled: !selectedSampleAssetIndex,
        isLoading: isLoading || isUploading,
      }}
      secondaryButtonProps={{
        disabled: isLoading,
      }}
      handlePrimaryButtonClick={async () => {
        if (selectedSampleAssetIndex && !isLoading && !isUploading) {
          setIsUploading(true);
          const selectedAsset = sampleAssets[selectedSampleAssetIndex - 1];
          await downloadAndUploadAsset(
            selectedAsset.video,
            selectedAsset.fileName,
            onboardingTempAnalyticsSource,
            processEventHandlers,
            () => {
              setIsUploading(false);
            }
          );
          uiInteraction({
            item_name: 'sample_video_selected',
            interaction_location: interactionLocation,
            interaction_id: uuid(),
            presentation_id: modalPresentationId,
            asset_id: selectedAsset.fileName,
          });
          setIsUploading(false);
        }
      }}
      secondaryButtonText={isShowingSampleAssets ? translate('Back') : ''}
      handleSecondaryButtonClick={() => {
        uiInteraction({
          item_name: 'back',
          interaction_location: interactionLocation,
          interaction_id: uuid(),
          presentation_id: modalPresentationId,
        });
        setIsShowingSampleAssets(false);
      }}
    >
      <>
        {isShowingSampleAssets ? (
          <SampleAssetsModal
            selectedAsset={selectedSampleAssetIndex}
            setSelectedAsset={(assetId) => {
              if (isLoading) {
                return;
              }
              setSelectedSampleAssetIndex(assetId);
              uiInteraction({
                item_name: 'sample_video_changed',
                interaction_location: interactionLocation,
                interaction_id: uuid(),
                presentation_id: modalPresentationId,
                ...(assetId && {
                  asset_id: sampleAssets[assetId - 1].fileName,
                }),
              });
            }}
          />
        ) : (
          <OnboardingFeatureModal
            showBestPractices={showBestPractices}
            feature={onboardingTempAnalyticsSource}
            assetErrors={assetErrors}
            onClickSampleAssets={() => {
              if (uploadedVideos.length) {
                // if currently uploading => do not allow to switch to sample assets
                return;
              }
              setIsShowingSampleAssets(true);
              contentLabActions.removeFeatureAssetErrors(
                onboardingTempAnalyticsSource
              );
              uiInteraction({
                item_name: 'explore_with_sample_video',
                interaction_location: interactionLocation,
                interaction_id: uuid(),
                presentation_id: modalPresentationId,
              });
            }}
            isLoading={isLoading}
          />
        )}
        {isShowingSampleAssets && (
          // We use this div as a divider between the sample assets and the buttons.
          // We can't use a divider component because it only extends to the width of the modal content container.
          <Box
            position="relative"
            height="1px"
            width="604px"
            bgcolor="#E6E6E6"
          />
        )}
      </>
    </Modal>
  );
}

export default OnboardingUploadModal;
